import analyticstracker from "@jmc/analyticstracker";
import { useCookieInteraction, usePageLoadedTracking } from "@jmc/core/src/hooks";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { useOnScreen } from "@jmc/utils/hooks/useOnScreen";
import React, { ReactNode, useEffect, useRef } from "react";

export interface BaseImpressionProps {
    children: ReactNode;
    /** The type of analytics event to fire for the impression. */
    eventType: EventTypes;
    /** An object containing the data to be sent with the tracking event. */
    trackingInfo: Record<string, unknown>;
    /** Optional configuration for the tracking behavior, passed to analyticstracker. */
    trackingOptions?: Record<string, unknown>;
    /** Delay in milliseconds before the tracking event is fired. */
    delay?: number;
    /** An array of additional boolean conditions that must all be true for tracking to occur. */
    additionalConditions?: boolean[];
    /** An optional callback function to execute when the impression is tracked. */
    onTrack?: () => void;
    /** If true, the impression is only tracked when the element is visible in the viewport. */
    checkVisibility?: boolean;
    /** An optional ref to be attached to the container element. */
    containerRef?: React.RefObject<HTMLDivElement>;
    /** Additional HTML attributes to be applied to the container element. */
    containerProps?: React.HTMLAttributes<HTMLDivElement>;
    /** If true, the impression event is fired every time the component enters the viewport. If false, it fires only once on page load. */
    trackRepeatedly?: boolean;
}

/**
 * 🏛️ BaseImpression is a versatile component for tracking user impressions on elements.
 * It wraps its children in a div and fires an analytics event when specific conditions are met.
 *
 * By default, it tracks an impression a single time when the page loads and the component
 * satisfies visibility and interaction conditions.
 *
 * It can be configured to track an impression every time the component becomes visible
 * in the viewport by setting `trackRepeatedly` to true.
 */
export const BaseImpression: React.FC<BaseImpressionProps> = ({
    children,
    eventType,
    trackingInfo,
    trackingOptions = {},
    delay = 0,
    additionalConditions = [],
    onTrack,
    checkVisibility = false,
    containerRef,
    containerProps = {},
    trackRepeatedly = false,
    ...rest
}) => {
    const { interacted } = useCookieInteraction();
    const internalRef = useRef<HTMLDivElement>(null);
    const ref = containerRef || internalRef;
    const isVisible = useOnScreen(ref);
    const prevIsVisibleRef = useRef<boolean>();

    const { shouldTrack, executeTracking } = usePageLoadedTracking({
        delay,
        conditions: [...(checkVisibility ? [isVisible] : []), interacted, ...additionalConditions],
        onTrack,
    });

    useEffect(() => {
        if (!trackRepeatedly && shouldTrack) {
            executeTracking(eventType, {
                ...trackingOptions,
            });
        }
    }, [trackRepeatedly, shouldTrack, eventType]);

    useEffect(() => {
        const allConditionsMet = !additionalConditions.some((condition) => !condition);
        if (trackRepeatedly && isVisible && !prevIsVisibleRef.current && interacted && allConditionsMet) {
            setTimeout(() => {
                analyticstracker().trackImpression(eventType, trackingOptions);
                onTrack?.();
            }, delay);
        }
        prevIsVisibleRef.current = isVisible;
    }, [trackRepeatedly, isVisible, interacted, additionalConditions, eventType, trackingOptions, delay, onTrack]);

    return (
        <div
            data-tracking-event={eventType}
            data-tracking-info={JSON.stringify(trackingInfo)}
            ref={ref}
            {...containerProps}
            {...rest}
        >
            {children}
        </div>
    );
};
